import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PackageX, Wrench, ExternalLink, Store, Warehouse, Clock, ClipboardList } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Modal, Button, Tag, Alert, StatusBadge, EntityAvatar, SectionHead, SECTION_TINT, type TagTone } from '../../ui';
import { request } from '../../api/client';
import { formatIst } from '../../lib/time';
import { fmtQty } from '../../lib/format';
import { orderStatusLabel } from '../../lib/statusTone';
import { WarehouseCode } from '../../lib/warehouseLabel';
import { CopyValue } from '../../components/CopyValue';
import { UserLink } from '../../components/EntityLinks';
import { usePermissionChecker } from '../../store/auth';
import { WarehouseCoveragePanel, liveShortfall } from './WarehouseCoveragePanel';
import {
  OUTCOME_LABEL, VIA_LABEL, RESOLUTION_LABEL, AGE_TONE, daysSince, daysOpen, parseSnapshot, type ShortfallRow,
} from './shortfallVocab';

/* ────────────────────────────────────────────────────────────────────────────────────────────────
 * HANDLING ONE SHORTFALL, WITHOUT LEAVING THE QUEUE.
 *
 * The queue told a planner everything about a blocked order except how to unblock it: the fix lived
 * on the order page, three screens away and below the money cards — so triaging four shortfalls meant
 * four round trips, and the row you came back to had moved. The fix itself is NOT re-implemented
 * here. `WarehouseCoveragePanel` is the app's one planning surface (the coverage matrix, the tax head
 * each site would produce, the transfer plan, and the Move / Transfer stock in / Add stock actions,
 * each gated by its own permission with the backend as the final authority); this dialog renders that
 * panel `bare` and puts the queue row's own facts above it, so the reader decides and acts in one place.
 *
 * The order page keeps the same panel, unchanged — one implementation, shown in two frames.
 * ──────────────────────────────────────────────────────────────────────────────────────────────── */

/** A labelled fact. SECTION / label / **value** are three visibly different levels — see SectionHead. */
function F({ label, icon, children }: { label: string; icon?: ReactNode; children?: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1.5 type-caption text-subtle">{icon}{label}</dt>
      <dd className="mt-1 min-w-0 type-body-strong text-content">{children ?? <span className="font-normal text-subtle">—</span>}</dd>
    </div>
  );
}

export function ShortfallActionModal({ row, open, onClose }: {
  row: ShortfallRow;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const hasPerm = usePermissionChecker();
  // The SAME permission reads the order page makes — the panel gates each action on them, and the
  // backend re-checks every one of them anyway.
  const canPlan = hasPerm('ORDER.DISPATCH.PLAN') || hasPerm('ORDER.EDIT');
  const canAddStock = hasPerm('STOCK.MASTER.ADD');
  const canReportShortfall = hasPerm('WAREHOUSE.PACK.START') || hasPerm('WAREHOUSE.PACK.COMPLETE') || canPlan;

  const orderId = String(row.orderId);
  const isOpen = row.status === 'OPEN';
  /* The footer slot the coverage panel puts its buttons into. The matrix is the tallest thing in
     this dialog, so an action parked under it is off-screen exactly when it is wanted — the reader
     has just scrolled down to tick a warehouse. A callback ref, because the panel needs the real
     node and the footer is rendered by the Modal, not by us. */
  const [actionsHost, setActionsHost] = useState<HTMLElement | null>(null);

  /* The order, for the transfers already raised against this shortfall. Without them the panel
     cannot tell "a consolidation is in flight" from "a consolidation arrived short and is blocked",
     and those are opposite instructions — wait, versus go and settle the discrepancy. Shares the
     cache key the order page uses, so opening the order afterwards costs nothing. */
  const { data: order } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => request<any>({ url: `/orders/${orderId}` }),
    enabled: open,
    retry: false,
  });

  /* The LIVE shortfall for this order — the very key the panel reads, so an action taken inside the
     panel clears this to null and the dialog says so, instead of going on describing a shortfall
     that no longer exists until it is reopened. */
  const { data: live, isFetched } = useQuery({
    queryKey: ['order-shortfall', orderId],
    queryFn: () => request<any>({ url: `/shortfalls/order/${orderId}` }),
    enabled: open && isOpen,
    retry: false,
  });
  const cleared = isOpen && isFetched && !live;

  /* The same coverage the panel below reads — React Query hands back the one cached response, so this
     costs no extra request. Without it the record band described the snapshot in the present tense
     ("WHAT IS SHORT — 80 × Biovin 20 kg") directly above a banner announcing the order is no longer
     short, and a reader had no way to tell which of the two to believe. */
  const { data: coverage } = useQuery({
    queryKey: ['order-coverage', orderId],
    queryFn: () => request<any>({ url: `/orders/${orderId}/warehouse-coverage` }),
    enabled: open && isOpen,
    retry: false,
  });
  // Still open in the queue, but the order itself is fine — the entry just hasn't been re-checked yet.
  const staleEntry = isOpen && !cleared && !!coverage && liveShortfall(coverage).resolved;

  // Prefer the live snapshot: the stored one is what was true when the row was raised.
  const lines = parseSnapshot(live?.shortSnapshot ?? row.shortSnapshot);
  const o = row.outcome ? OUTCOME_LABEL[row.outcome] : null;
  /* An open row is still running, so it counts from when it was raised; a closed one is a finished
     span, raised → resolved. Same label, two different questions — so two different measurements. */
  const age = isOpen ? daysSince(row.reportedAt) : daysOpen(row.reportedAt, row.resolvedAt);
  const closedAge = daysSince(row.resolvedAt);
  const whName = row.warehouseName && row.warehouseName.toLowerCase() !== (row.warehouseCode ?? '').toLowerCase()
    ? row.warehouseName : null;
  const stage = order?.status ?? row.orderStatus;

  return (
    <Modal
      open={open}
      onOpenChange={(v) => { if (!v) onClose(); }}
      /* Wide enough that the coverage matrix does not have to be dragged.
         The matrix's min-content width is ~980px (warehouse, ships-from, GST, coverage, items
         covered, short). A dialog spends `width` on 2px of border, ~15px of vertical scrollbar and
         40px of body padding before the table sees any of it, so a 980px dialog handed the table 921
         and it panned. This gives it ~1,020 — the table fits, with slack for a longer site name.
         Still capped at `100vw - 2rem` by the kit, so a narrow screen pans as before. */
      width={1080}
      // A record, not a form: nothing here is typed into, and the first focusable child is the
      // click-to-copy order number in the title — focusing it popped its tooltip over the header.
      autoFocus={false}
      title={<span className="flex min-w-0 flex-wrap items-center gap-2">
        Shortfall
        <CopyValue value={row.orderNo} label="Order no" />
      </span>}
      description={[row.retailerName, whName ?? row.warehouseCode].filter(Boolean).join(' · ') || undefined}
      footer={<>
        {/* Filled by WarehouseCoveragePanel via `actionsPortal`; `mr-auto` keeps the dialog's own
            two buttons on the right however many actions the panel puts here. */}
        <div ref={setActionsHost} className="mr-auto flex flex-wrap items-center gap-2" />
        <Button variant="default" onClick={onClose}>Close</Button>
        <Button variant="primary" icon={<ExternalLink size={16} />} onClick={() => navigate(`/orders/${orderId}`)}>
          Open the full order
        </Button>
      </>}
    >
      <div className="space-y-4">
        {!isOpen && (
          <Alert
            type="success"
            title={closedAge === null ? 'Resolved — nothing to do here.'
              : closedAge === 0 ? 'Resolved today — nothing to do here.' : `Resolved ${closedAge}d ago — nothing to do here.`}
            description={(row.resolution && RESOLUTION_LABEL[row.resolution]) ?? 'It cleared on its own, the way every entry in this queue does.'}
          />
        )}
        {cleared && (
          <Alert
            type="success"
            title="Cleared — this order is no longer short."
            description="The entry closes itself, so it has already gone from the list behind this dialog."
          />
        )}

        <section>
          {/* Past tense the moment it stops being true — see `staleEntry`. */}
          <SectionHead icon={<PackageX size={14} />} tint={SECTION_TINT.system}>
            {isOpen && !staleEntry && !cleared ? 'What is short' : 'What was short'}
          </SectionHead>
          {lines.length ? (
            <div className="flex flex-wrap gap-1.5">
              {/* Each product name carries its pack ("Biovin 20 kg") — `Biovin` alone is two different
                  products here, and a chip that doesn't say which one cannot be acted on. */}
              {lines.map((l, i) => (
                // Red states a live problem. Once the order is covered these are a record of one.
                <Tag tone={isOpen && !staleEntry && !cleared ? 'red' : 'neutral'} key={`${l.productId}-${i}`}>
                  {fmtQty(Number(l.shortQty))} × {l.productName ?? l.productId}
                </Tag>
              ))}
            </div>
          ) : (
            <Tag tone={isOpen ? 'red' : 'neutral'}>short {fmtQty(Number(row.pendingTotal))}</Tag>
          )}

          <dl className="mt-3.5 grid grid-cols-2 gap-x-4 gap-y-3.5 lg:grid-cols-4">
            <F label={isOpen && !staleEntry && !cleared ? 'Short at' : 'Was short at'} icon={<Warehouse size={12} />}>
              <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                {row.warehouseCode ? <WarehouseCode code={row.warehouseCode} /> : null}
                {whName ? <span className="truncate">{whName}</span> : null}
                {!row.warehouseCode && !whName ? `#${row.warehouseId}` : null}
              </span>
            </F>
            <F label="Order stage" icon={<Store size={12} />}>
              {stage ? <StatusBadge status={stage} label={orderStatusLabel(stage)} /> : undefined}
            </F>
            <F label={isOpen && !staleEntry && !cleared ? 'Blocking for' : 'Was open'} icon={<Clock size={12} />}>
              {age == null ? undefined : (
                <Tag tone={isOpen ? AGE_TONE(age) : ('neutral' as TagTone)}>
                  {age > 0 ? `${age}d` : isOpen ? 'today' : 'under a day'}
                </Tag>
              )}
            </F>
            <F label="Raised" icon={<ClipboardList size={12} />}>
              <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                <Tag tone={row.reportedVia === 'MANUAL' ? 'blue' : 'neutral'}>{VIA_LABEL[row.reportedVia] ?? row.reportedVia}</Tag>
                {row.reportedByName && (
                  <span className="inline-flex min-w-0 items-center gap-1.5">
                    <EntityAvatar name={row.reportedByName} size={20} />
                    <span className="truncate"><UserLink id={row.reportedById}>{row.reportedByName}</UserLink></span>
                  </span>
                )}
              </span>
              {row.reportedAt && <div className="mt-0.5 type-caption font-normal text-subtle">{formatIst(row.reportedAt)}</div>}
            </F>
          </dl>

          {/* The reporter's own words WRAP — this is the one free-text field on the record, and the
              register could only give it a truncated line. */}
          {row.remarks && (
            <blockquote className="mt-3 rounded-control border-l-2 border-brand-400/60 bg-surface-2/60 px-3 py-2 type-body italic text-muted">
              “{row.remarks}”
            </blockquote>
          )}
        </section>

        {isOpen && (
          <section>
            {/* The outcome names the JOB; the panel's own banner below names the destination and
                carries the button that does it, so the instruction is written once. */}
            <SectionHead
              icon={<Wrench size={14} />} tint={SECTION_TINT.assignment}
              hint={o ? <Tag tone={o.color as TagTone} icon={<o.Icon size={11} />}>{o.text}</Tag> : undefined}
            >
              How to fix it
            </SectionHead>
            <WarehouseCoveragePanel
              bare
              orderId={orderId}
              // The order's CURRENT dispatch warehouse: on an open row that is the site it is short
              // at (the queue retires a row whose warehouse moved), but read the live order first.
              dispatchWarehouseId={order?.dispatchWarehouseId ?? row.currentWarehouseId ?? row.warehouseId}
              canPlan={canPlan}
              canAddStock={canAddStock}
              canReportShortfall={canReportShortfall}
              orderStatus={stage}
              transfers={(order as any)?.transfers}
              actionsPortal={actionsHost}
              // Acting in here changes the queue behind the dialog — the row clears the moment the
              // order stops being short, so the list must not go on showing it.
              onChanged={() => {
                qc.invalidateQueries({ queryKey: ['shortfalls'] });
                qc.invalidateQueries({ queryKey: ['badges'] });
              }}
            />
          </section>
        )}
      </div>
    </Modal>
  );
}
